// Customer Prices data layer (Phase 4, P4-WP11c) — the highest pricing tier: a price
// negotiated for ONE customer. The SINGLE interface between the app and
// public.customer_prices (supabase/migrations/20260722120000_customer_prices.sql).
//
// Rows are keyed by (customer, product, unit-or-base, min_qty); min_qty is
// quantity-break-ready. Unit-ownership (the unit must belong to the product) is enforced
// by the DB composite FK (product_id, unit_id) → product_units(product_id, id) — no
// data-layer existence check; its 23503 maps to a friendly message. company_id is
// inherited from the parent customer + frozen by DB triggers; we never send it.

import { supabase } from "./supabaseClient";

const CP = "customer_prices";
const SELECT = "id, customer_id, product_id, unit_id, min_qty, unit_price";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23505": message = "This customer already has a price for that product/unit/quantity."; break;
    case "23514": message = "A value is out of range (min quantity must be ≥ 1, price ≥ 0)."; break;
    case "23503": message = "That unit does not belong to this product."; break;   // composite FK
    case "23502": message = "A required field is missing."; break;
    case "42501": message = "You do not have access to this customer."; break;
    case "PGRST116": message = "Price not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

/** A customer's negotiated prices, ordered by product then min_qty. */
export async function getCustomerPrices(customerId) {
  const { data, error } = await client().from(CP).select(SELECT)
    .eq("customer_id", customerId)
    .order("product_id", { ascending: true }).order("min_qty", { ascending: true });
  if (error) throw toError(error, "Could not load customer prices");
  return data || [];
}

export async function addCustomerPrice(customerId, { product_id, unit_id = null, min_qty = 1, unit_price } = {}) {
  if (!product_id) throw new Error("A product is required.");
  if (unit_price == null || unit_price === "") throw new Error("A unit price is required.");
  const row = { customer_id: customerId, product_id, unit_id: unit_id || null, min_qty: Number(min_qty) || 1, unit_price: Number(unit_price) };
  const { data, error } = await client().from(CP).insert(row).select(SELECT).single();
  if (error) throw toError(error, "Could not add the customer price");
  return data;
}

export async function updateCustomerPrice(id, patch = {}) {
  const upd = {};
  if (patch.product_id !== undefined) upd.product_id = patch.product_id;
  if (patch.unit_id !== undefined) upd.unit_id = patch.unit_id || null;
  if (patch.min_qty !== undefined) upd.min_qty = Number(patch.min_qty) || 1;
  if (patch.unit_price !== undefined) upd.unit_price = Number(patch.unit_price);
  const { data, error } = await client().from(CP).update(upd).eq("id", id).select(SELECT).single();
  if (error) throw toError(error, "Could not update the customer price");
  return data;
}

export async function removeCustomerPrice(id) {
  const { error } = await client().from(CP).delete().eq("id", id);
  if (error) throw toError(error, "Could not remove the customer price");
}
